import { Activity, BarChart3, PieChart, Table } from 'lucide-react';
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { PageLayout } from '@/components/Layout/PageLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/Common/Card';
import { useIsSuperAdmin, useIsOrganizationAdmin } from '@/stores/authStore';
import llmUsageService from '@/services/llmUsageService';
import organizationService from '@/services/organizationService';
import { fetchAllUsers } from '@/services/userService';
import type { LLMUsageStats, LLMUsageLogEntry } from '@/types';
import type { Organization } from '@/types';
import type { User } from '@/types';
import { DoughnutChart } from '@/components/Dashboard/DoughnutChart';
import TokenEconomyPanel from './TokenEconomyPanel';

const TASK_OPTIONS: { value: string; labelKey: string }[] = [
  { value: '', labelKey: 'admin.tokenConsumption.allTasks' },
  { value: 'import_doc', labelKey: 'admin.tokenConsumption.taskImportDoc' },
  { value: 'generate_report', labelKey: 'admin.tokenConsumption.taskGenerateReport' },
  { value: 'chat', labelKey: 'admin.tokenConsumption.taskChat' },
  { value: 'fill_form', labelKey: 'admin.tokenConsumption.taskFillForm' },
  { value: 'rag_chat', labelKey: 'admin.tokenConsumption.taskRagChat' },
  { value: 'rag_embedding', labelKey: 'admin.tokenConsumption.taskRagEmbedding' },
  { value: 'indexing_embedding', labelKey: 'admin.tokenConsumption.taskIndexingEmbedding' },
  { value: 'ontology_extraction', labelKey: 'admin.tokenConsumption.taskOntologyExtraction' },
  { value: 'starter_questions', labelKey: 'admin.tokenConsumption.taskStarterQuestions' },
  { value: 'artifact_chat', labelKey: 'admin.tokenConsumption.taskArtifactChat' },
  { value: 'generate_file', labelKey: 'admin.tokenConsumption.taskGenerateFile' },
  { value: 'cowork_handoff', labelKey: 'admin.tokenConsumption.taskCoworkHandoff' },
  { value: 'project_file_selection', labelKey: 'admin.tokenConsumption.taskProjectFileSelection' },
  { value: 'project_concepts', labelKey: 'admin.tokenConsumption.taskProjectConcepts' },
  { value: 'project_analysis', labelKey: 'admin.tokenConsumption.taskProjectAnalysis' },
  { value: 'portfolio_map_naming', labelKey: 'admin.tokenConsumption.taskPortfolioMapNaming' },
  { value: 'portfolio_embedding', labelKey: 'admin.tokenConsumption.taskPortfolioEmbedding' },
  { value: 'unknown', labelKey: 'admin.tokenConsumption.taskUnknown' },
];

const TASK_TO_LABEL_KEY: Record<string, string> = {
  import_doc: 'admin.tokenConsumption.taskImportDoc',
  generate_report: 'admin.tokenConsumption.taskGenerateReport',
  chat: 'admin.tokenConsumption.taskChat',
  fill_form: 'admin.tokenConsumption.taskFillForm',
  rag_chat: 'admin.tokenConsumption.taskRagChat',
  rag_embedding: 'admin.tokenConsumption.taskRagEmbedding',
  indexing_embedding: 'admin.tokenConsumption.taskIndexingEmbedding',
  ontology_extraction: 'admin.tokenConsumption.taskOntologyExtraction',
  starter_questions: 'admin.tokenConsumption.taskStarterQuestions',
  artifact_chat: 'admin.tokenConsumption.taskArtifactChat',
  generate_file: 'admin.tokenConsumption.taskGenerateFile',
  cowork_handoff: 'admin.tokenConsumption.taskCoworkHandoff',
  project_file_selection: 'admin.tokenConsumption.taskProjectFileSelection',
  project_concepts: 'admin.tokenConsumption.taskProjectConcepts',
  project_analysis: 'admin.tokenConsumption.taskProjectAnalysis',
  portfolio_map_naming: 'admin.tokenConsumption.taskPortfolioMapNaming',
  portfolio_embedding: 'admin.tokenConsumption.taskPortfolioEmbedding',
  unknown: 'admin.tokenConsumption.taskUnknown',
};

const PAGE_SIZE = 20;

const CHART_COLORS = [
  'rgba(59, 130, 246, 0.8)',   // blue
  'rgba(34, 197, 94, 0.8)',    // green
  'rgba(234, 179, 8, 0.8)',    // amber
  'rgba(239, 68, 68, 0.8)',    // red
  'rgba(168, 85, 247, 0.8)',   // purple
  'rgba(236, 72, 153, 0.8)',   // pink
  'rgba(20, 184, 166, 0.8)',   // teal
  'rgba(249, 115, 22, 0.8)',   // orange
];

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

/** Format large numbers as 1.2k, 3.5M, etc. */
function formatCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
  return new Intl.NumberFormat().format(n);
}

/** Start of day (00:00:00) and end of day (23:59:59.999) in local time, then to ISO for API */
function getDateRange(dateRange: '7' | '30' | 'all'): { date_from?: string; date_to?: string } {
  if (dateRange === 'all') return {};
  const now = new Date();
  const from = new Date(now);
  if (dateRange === '7') from.setDate(from.getDate() - 7);
  else from.setDate(from.getDate() - 30);
  from.setHours(0, 0, 0, 0);
  const to = new Date(now);
  to.setHours(23, 59, 59, 999);
  return {
    date_from: from.toISOString().slice(0, 19) + 'Z',
    date_to: to.toISOString().slice(0, 19) + 'Z',
  };
}

type TabId = 'overview' | 'logs' | 'economy';

const TokenConsumptionPage: React.FC = () => {
  const { t } = useTranslation();
  const isSuperAdmin = useIsSuperAdmin();
  // Org admins can view their own organization's usage; super admins see everything.
  const canView = useIsOrganizationAdmin();

  const [stats, setStats] = useState<LLMUsageStats | null>(null);
  const [logs, setLogs] = useState<LLMUsageLogEntry[]>([]);
  const [totalLogs, setTotalLogs] = useState(0);
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [taskFilter, setTaskFilter] = useState('');
  const [organizationFilter, setOrganizationFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [dateRange, setDateRange] = useState<'7' | '30' | 'all'>('30');
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [users, setUsers] = useState<User[]>([]);

  const { date_from: dateFrom, date_to: dateTo } = getDateRange(dateRange);

  const orgId = organizationFilter || undefined;
  const userId = userFilter || undefined;

  const loadStats = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    setError(null);
    try {
      const data = await llmUsageService.getStats({
        date_from: dateFrom,
        date_to: dateTo,
        organization_id: orgId,
        user_id: userId,
      });
      setStats(data);
    } catch (e: unknown) {
      setError((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? t('admin.tokenConsumption.statsError', 'Failed to load stats'));
    } finally {
      setLoading(false);
    }
  }, [canView, dateFrom, dateTo, orgId, userId, t]);

  const loadLogs = useCallback(async () => {
    if (!canView) return;
    setLogsLoading(true);
    try {
      const skip = (page - 1) * PAGE_SIZE;
      const data = await llmUsageService.getLogs({
        skip,
        limit: PAGE_SIZE,
        task: taskFilter || undefined,
        organization_id: orgId,
        user_id: userId,
        date_from: dateFrom,
        date_to: dateTo,
      });
      setLogs(data.items);
      setTotalLogs(data.total);
    } catch (e: unknown) {
      setError((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? t('admin.tokenConsumption.logsError', 'Failed to load logs'));
    } finally {
      setLogsLoading(false);
    }
  }, [canView, page, taskFilter, orgId, userId, dateFrom, dateTo, t]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    if (activeTab === 'logs') loadLogs();
  }, [activeTab, loadLogs]);

  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    (async () => {
      try {
        // fetchAllUsers is org-scoped server-side for non-super-admins.
        // The organization filter is only meaningful (and authorized) for super admins.
        const [orgs, userList] = await Promise.all([
          isSuperAdmin ? organizationService.getOrganizations() : Promise.resolve<Organization[]>([]),
          fetchAllUsers({ limit: 1000 }),
        ]);
        if (!cancelled) {
          setOrganizations(orgs);
          setUsers(userList);
        }
      } catch {
        if (!cancelled) {
          setOrganizations([]);
          setUsers([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [canView, isSuperAdmin]);

  const filteredUsers = organizationFilter
    ? users.filter((u) => u.organization_id === organizationFilter)
    : users;
  useEffect(() => {
    if (userFilter && organizationFilter) {
      const selectedUser = users.find((u) => u.id === userFilter);
      if (selectedUser?.organization_id !== organizationFilter) setUserFilter('');
    }
  }, [organizationFilter, userFilter, users]);

  const totalPages = Math.max(1, Math.ceil(totalLogs / PAGE_SIZE));

  const getTaskDisplayName = (taskSlug: string): string => {
    const key = TASK_TO_LABEL_KEY[taskSlug] ?? 'admin.tokenConsumption.taskUnknown';
    return t(key);
  };

  const buildPieData = (
    data: Record<string, number> | undefined,
    total: number,
    labelFn: (key: string) => string = (k) => k,
    fallbackLabelKey?: string
  ) => {
    if (total === 0) return { labels: [t('admin.tokenConsumption.noData', 'No data')], datasets: [{ data: [1], backgroundColor: ['rgba(128,128,128,0.5)'] }] };
    const entries = data ? Object.entries(data).filter(([, v]) => v > 0) : [];
    if (entries.length === 0) {
      // When we have total tokens but no breakdown (e.g. by_model empty), show one slice = 100%
      return {
        labels: [fallbackLabelKey ? t(fallbackLabelKey) : t('admin.tokenConsumption.allModels', 'All models')],
        datasets: [{ data: [total], backgroundColor: [CHART_COLORS[0]] }],
      };
    }
    return {
      labels: entries.map(([k]) => labelFn(k)),
      datasets: [{
        data: entries.map(([, v]) => v),
        backgroundColor: entries.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]),
      }],
    };
  };

  if (!canView) {
    return (
      <div className="w-full flex items-center justify-center py-16">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="text-xl flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-muted-foreground" />
              {t('admin.tokenConsumption.unauthorizedTitle', 'Unauthorized')}
            </CardTitle>
            <CardDescription>
              {t('admin.tokenConsumption.unauthorizedDescription', 'Only administrators can access this page.')}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const tabs: { id: TabId; labelKey: string; icon: React.ElementType }[] = [
    { id: 'overview', labelKey: 'admin.tokenConsumption.tabOverview', icon: PieChart },
    { id: 'logs', labelKey: 'admin.tokenConsumption.tabLogs', icon: Table },
    // Token economy manages platform-wide quota policy (system default + per-org
    // overrides across all organizations) — a super-admin-only capability.
    ...(isSuperAdmin
      ? [{ id: 'economy' as TabId, labelKey: 'admin.tokenConsumption.tabEconomy', icon: Activity }]
      : []),
  ];

  const totalTokens = stats?.total_tokens ?? 0;
  const pieByModel = buildPieData(stats?.by_model, totalTokens, (k) => k, 'admin.tokenConsumption.allModels');
  const pieByProvider = buildPieData(stats?.by_provider, totalTokens, (k) => k, 'admin.tokenConsumption.allProviders');
  const pieByTask = buildPieData(stats?.by_task, totalTokens, getTaskDisplayName, 'admin.tokenConsumption.allTasks');

  return (
    <PageLayout
      icon={<BarChart3 className="h-6 w-6" />}
      title={t('admin.tokenConsumption.title', 'Token consumption')}
      subtitle={t('admin.tokenConsumption.subtitle', 'LLM token usage and cost visibility')}
    >
      <div className="space-y-6">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4">
          <span className="text-sm text-muted-foreground">{t('admin.tokenConsumption.period', 'Period')}:</span>
          <select
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={dateRange}
            onChange={(e) => { setDateRange(e.target.value as '7' | '30' | 'all'); setPage(1); }}
          >
            <option value="7">{t('admin.tokenConsumption.last7Days', 'Last 7 days')}</option>
            <option value="30">{t('admin.tokenConsumption.last30Days', 'Last 30 days')}</option>
            <option value="all">{t('admin.tokenConsumption.allTime', 'All time')}</option>
          </select>
          {isSuperAdmin && (
            <>
              <span className="text-sm text-muted-foreground">{t('admin.tokenConsumption.filterByOrganization', 'Organization')}:</span>
              <select
                className="rounded-md border border-input bg-background px-3 py-2 text-sm min-w-[180px]"
                value={organizationFilter}
                onChange={(e) => { setOrganizationFilter(e.target.value); setPage(1); }}
              >
                <option value="">{t('admin.tokenConsumption.allOrganizations', 'All organizations')}</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
            </>
          )}
          <span className="text-sm text-muted-foreground">{t('admin.tokenConsumption.filterByUser', 'User')}:</span>
          <select
            className="rounded-md border border-input bg-background px-3 py-2 text-sm min-w-[200px]"
            value={userFilter}
            onChange={(e) => { setUserFilter(e.target.value); setPage(1); }}
          >
            <option value="">{t('admin.tokenConsumption.allUsers', 'All users')}</option>
            {filteredUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name || u.email}
                </option>
              ))}
          </select>
        </div>

        {error && (
          <div className="rounded-md bg-destructive/10 text-destructive px-4 py-2 text-sm">
            {error}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 border-b border-border">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setActiveTab(tab.id)}
            >
              <tab.icon className="h-4 w-4" />
              {t(tab.labelKey)}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Stats cards */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      {t('admin.tokenConsumption.totalTokens', 'Total tokens')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <span className="text-2xl font-semibold">{formatCompact(stats.total_tokens)}</span>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      {t('admin.tokenConsumption.promptTokens', 'Prompt tokens')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <span className="text-2xl font-semibold">{formatCompact(stats.total_prompt_tokens)}</span>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      {t('admin.tokenConsumption.completionTokens', 'Completion tokens')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <span className="text-2xl font-semibold">{formatCompact(stats.total_completion_tokens)}</span>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      {t('admin.tokenConsumption.byProvider', 'By provider')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm space-y-1">
                      {stats.by_provider && Object.keys(stats.by_provider).length > 0
                        ? Object.entries(stats.by_provider).map(([prov, tot]) => (
                            <div key={prov}>{prov}: {formatCompact(tot)}</div>
                          ))
                        : t('admin.tokenConsumption.noData', 'No data')}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {loading && !stats && (
              <p className="text-muted-foreground">{t('common.loading', 'Loading…')}</p>
            )}

            {/* Pie charts */}
            {stats && totalTokens > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <DoughnutChart
                  data={pieByModel}
                  title={t('admin.tokenConsumption.chartByModel', '% by model')}
                  alwaysShowChart
                />
                <DoughnutChart
                  data={pieByProvider}
                  title={t('admin.tokenConsumption.chartByProvider', '% by provider')}
                  alwaysShowChart
                />
                <DoughnutChart
                  data={pieByTask}
                  title={t('admin.tokenConsumption.chartByTask', '% by task type')}
                  alwaysShowChart
                />
              </div>
            )}
          </div>
        )}

        {activeTab === 'logs' && (
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <CardTitle>{t('admin.tokenConsumption.logsTitle', 'Usage logs')}</CardTitle>
                  <CardDescription>
                    {t('admin.tokenConsumption.logsDescription', 'All LLM calls with user and task.')}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">{t('admin.tokenConsumption.task', 'Task')}:</span>
                  <select
                    className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={taskFilter}
                    onChange={(e) => { setTaskFilter(e.target.value); setPage(1); }}
                  >
                    {TASK_OPTIONS.map((opt) => (
                      <option key={opt.value || 'all'} value={opt.value}>{t(opt.labelKey)}</option>
                    ))}
                  </select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {logsLoading && logs.length === 0 ? (
                <p className="text-muted-foreground py-4">{t('common.loading', 'Loading…')}</p>
              ) : logs.length === 0 ? (
                <p className="text-muted-foreground py-4">{t('admin.tokenConsumption.noLogs', 'No usage logs in this period.')}</p>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-2">{t('admin.tokenConsumption.table.date', 'Date')}</th>
                          <th className="text-left py-2 px-2">{t('admin.tokenConsumption.table.user', 'User')}</th>
                          <th className="text-left py-2 px-2">{t('admin.tokenConsumption.table.task', 'Task')}</th>
                          <th className="text-left py-2 px-2">{t('admin.tokenConsumption.table.provider', 'Provider')}</th>
                          <th className="text-left py-2 px-2">{t('admin.tokenConsumption.table.model', 'Model')}</th>
                          <th className="text-right py-2 px-2">{t('admin.tokenConsumption.table.promptTokens', 'Prompt')}</th>
                          <th className="text-right py-2 px-2">{t('admin.tokenConsumption.table.completionTokens', 'Completion')}</th>
                          <th className="text-right py-2 px-2">{t('admin.tokenConsumption.table.totalTokens', 'Total')}</th>
                          <th className="text-left py-2 px-2">{t('admin.tokenConsumption.table.organization', 'Organization')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {logs.map((row) => (
                          <tr key={row.id} className="border-b border-muted/50">
                            <td className="py-2 px-2">{formatDate(row.created_at)}</td>
                            <td className="py-2 px-2">{row.user_email ?? row.user_name ?? '—'}</td>
                            <td className="py-2 px-2">{getTaskDisplayName(row.task)}</td>
                            <td className="py-2 px-2">{row.provider}</td>
                            <td className="py-2 px-2">{row.model}</td>
                            <td className="text-right py-2 px-2">{formatCompact(row.prompt_tokens)}</td>
                            <td className="text-right py-2 px-2">{formatCompact(row.completion_tokens)}</td>
                            <td className="text-right py-2 px-2">{formatCompact(row.total_tokens)}</td>
                            <td className="py-2 px-2">{row.organization_name ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-sm text-muted-foreground">
                      {t('admin.tokenConsumption.pagination.showing', 'Showing {{from}}–{{to}} of {{total}}', {
                        from: totalLogs === 0 ? 0 : (page - 1) * PAGE_SIZE + 1,
                        to: Math.min(page * PAGE_SIZE, totalLogs),
                        total: totalLogs,
                      })}
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="rounded border px-3 py-1 text-sm disabled:opacity-50"
                        disabled={page <= 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                      >
                        {t('common.previous', 'Previous')}
                      </button>
                      <span className="flex items-center px-2 text-sm">
                        {t('admin.tokenConsumption.pagination.page', 'Page {{current}} of {{total}}', { current: page, total: totalPages })}
                      </span>
                      <button
                        type="button"
                        className="rounded border px-3 py-1 text-sm disabled:opacity-50"
                        disabled={page >= totalPages}
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      >
                        {t('common.next', 'Next')}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 'economy' && isSuperAdmin && <TokenEconomyPanel />}
      </div>
    </PageLayout>
  );
};

export default TokenConsumptionPage;
